import { Button, ButtonWithLoader } from '@neo/primitives/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@neo/primitives/dialog'
import { useQueryClient } from '@tanstack/react-query'
import { Banknote, CreditCard, Landmark } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import {
  useBillingCardsQuery,
  useCashfreeCreateOrderMutation,
  useCashfreePayMutation,
  useEnsureActiveVbank,
} from 'v2/pages/billing/queries'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { useUserPlan } from 'v2/shared/hooks/use-plan'
import { formatCurrency } from 'v2/shared/utils'
import { AnalyticsEvents, track } from 'v2/utils/analytics'
import { logger } from 'v2/utils/logger'
import { errorToast, infoToast, successToast } from 'v2/utils/toasts'
import { Currency } from '../billing.config'
import { getCashfree } from '../cashfree-sdk'
import { AddCardModal } from '../components/add-card-modal'
import { AddFirstCard } from '../components/add-first-card'
import { resolveSelectedCardId } from '../components/card-selection'
import { CardSelector } from '../components/card-selector'
import { MethodTile } from '../components/method-tile'
import { RechargeAmountTile } from '../components/recharge-amount-tile'
import { useBillingAddressQuery, useBillingRechargeDetailsQuery } from '../queries'
import {
  buildPaymentCallbackUrl,
  getBankRailDisabledReason,
  getCardFeeLabel,
  getNetbankingRechargeChoices,
  getSortedRechargeChoices,
  isCashfreePaid,
  isCashfreeUserCancelled,
  launchCashfreeHostedCheckout,
  verifyAndPollCashfreePayment,
  ZERO_FEE_LABEL,
} from '../recharge-shared'
import {
  BILLING_REFRESH_PROCESSING_MAX_POLL_ATTEMPTS,
  refreshBillingAfterPayment,
} from '../refresh-billing-queries'
import { CardState } from '../types'
import { buildUpgradePlanPatch } from '../upgrade-plan'
import { useIsCashfreeProvider } from '../use-billing-provider'
import { getAccountId, trackPaymentFailed } from '../utils'
import { CashfreeBankTransferDrawer } from './cashfree-bank-transfer-drawer'
import { resolveRechargeCtaBlocker } from './recharge-cta'
import {
  isRazorpaySavedCard,
  rechargeGatewayLabel,
  useRazorpayStandardCheckout,
} from './use-razorpay-standard-checkout'

// Cashfree PG Orders flow — card / netbanking hand a payment_session_id to
// the Cashfree.js SDK which renders the hosted checkout; bank transfer uses
// a virtual account.
type Method = 'card' | 'netbanking' | 'bank_transfer'

interface CashfreeRechargeModalProps {
  isOpen: boolean
  onClose: () => void
  // When set, only the chosen method is selectable and the picker is hidden —
  // used by Settings → Payment Methods where the entry-point tile already
  // declares the intent (clicking "Add Card" shouldn't reveal netbanking).
  restrictToMethod?: Method
}

export const CashfreeRechargeModal = ({
  isOpen,
  onClose,
  restrictToMethod,
}: CashfreeRechargeModalProps) => {
  const accountId = getAccountId()
  const { isEnterprisePlan, isTrial } = useUserPlan()
  const { data: rechargeDetails } = useBillingRechargeDetailsQuery()
  const currency = rechargeDetails?.currency || Currency.INR
  // Per-org bank-transfer gate (defaults false until hodor enables it).
  // `isCashFreeTesting` is an internal-testing-only flag that strictly defaults
  // to false; when true it bypasses BOTH bank rails' floors so QA can
  // verify the flow at any amount.
  const { isCashFreeBankTransferEnabled, isCashFreeTesting, isEmbeddedCardCheckoutEnabled } =
    useFeatureFlags()
  // Spread first — Array.prototype.sort mutates, and recharge_choices is the
  // React Query cache reference.
  const sortedRechargeChoices = useMemo(
    () => getSortedRechargeChoices(rechargeDetails?.recharge_choices),
    [rechargeDetails?.recharge_choices],
  )
  // The netbanking screen only offers amounts netbanking will settle (≥ ₹10,000 —
  // Cashfree's merchant-account limit, SER-5416); card / bank-transfer keep the
  // full set and disable the tile instead.
  const tileChoices = useMemo(
    () =>
      restrictToMethod === 'netbanking'
        ? getNetbankingRechargeChoices(sortedRechargeChoices, currency, isCashFreeTesting)
        : sortedRechargeChoices,
    [restrictToMethod, sortedRechargeChoices, currency, isCashFreeTesting],
  )

  const defaultSelectable = tileChoices?.[0] ?? 0

  const [userSelectedAmount, setUserSelectedAmount] = useState<number | null>(null)
  const selectedAmount = userSelectedAmount ?? defaultSelectable
  const [userSelectedMethod, setUserSelectedMethod] = useState<Method>(restrictToMethod ?? 'card')
  const [bankTransferOpen, setBankTransferOpen] = useState(false)
  const [isAddCardOpen, setIsAddCardOpen] = useState(false)
  const isCashfreeProvider = useIsCashfreeProvider()

  // One reason per rail. Both floors are ₹10,000 today but for independent
  // reasons (Cashfree's merchant limit vs the virtual-account settlement rule),
  // so they are read separately — see recharge-shared.
  const netbankingDisabledReason = getBankRailDisabledReason(
    'netbanking',
    selectedAmount,
    currency,
    isCashFreeTesting,
  )
  const bankTransferDisabledReason = getBankRailDisabledReason(
    'bank_transfer',
    selectedAmount,
    currency,
    isCashFreeTesting,
  )
  // The reason for the rail the customer picked; card has no floor.
  const selectedRailDisabledReason =
    userSelectedMethod === 'netbanking'
      ? netbankingDisabledReason
      : userSelectedMethod === 'bank_transfer'
        ? bankTransferDisabledReason
        : undefined

  // When opened from a payment-methods tile, the method is locked. Otherwise
  // fall back to card if the selected bank-rails method is below the min amount;
  // re-engages if the amount changes.
  const method: Method = (() => {
    if (restrictToMethod) return restrictToMethod
    if (userSelectedMethod !== 'card' && selectedRailDisabledReason) return 'card'
    return userSelectedMethod
  })()

  // Cashfree-synced cards land in the shared cards table, so the existing
  // /v1/account/card endpoint returns them too.
  const { data: cards } = useBillingCardsQuery()
  const { data: billingInformation } = useBillingAddressQuery()
  const isFirstCardAdd = (cards?.length ?? 0) === 0 && !billingInformation?.zipcode
  const activeCards = useMemo(
    () => (cards ?? []).filter((c) => c.state === CardState.ACTIVE),
    [cards],
  )
  const [selectedCardId, setSelectedCardId] = useState<number>(0)
  // Shared with the other checkout modals: the picker can delete the selected
  // card, so the effective id has to be derived, not stored.
  const effectiveSelectedCardId = useMemo(
    () => resolveSelectedCardId(activeCards, selectedCardId),
    [activeCards, selectedCardId],
  )

  const queryClient = useQueryClient()
  const { mutateAsync: createOrder, isPending: creatingOrder } = useCashfreeCreateOrderMutation()
  const ensureActiveVbank = useEnsureActiveVbank()
  const { mutateAsync: cashfreePay, isPending: cashfreePayPending } = useCashfreePayMutation()
  const [isPreparingBankTransfer, setIsPreparingBankTransfer] = useState(false)
  const [isLaunchingCheckout, setIsLaunchingCheckout] = useState(false)

  // Warm the Cashfree JS SDK as soon as the modal opens so the hosted checkout
  // launches instantly on Pay instead of cold-loading the CDN script only after
  // the order-create round-trip. getCashfree() is memoized + idempotent.
  //
  // Gated on the reachable use, NOT on isCashfreeProvider alone: every INR
  // account now resolves to Razorpay, so that flag is permanently false here —
  // yet this modal is still the netbanking implementation for all of them
  // (payment-modes mounts it with restrictToMethod="netbanking" regardless of
  // provider). Warming on the provider alone would silently retire the very
  // optimization this effect exists for on its only remaining caller.
  const shouldWarmCashfree = isCashfreeProvider || restrictToMethod === 'netbanking'
  useEffect(() => {
    if (isOpen && shouldWarmCashfree) getCashfree().catch(() => {})
  }, [isOpen, shouldWarmCashfree])

  // Reset + close, WITHOUT the inflight guard below. Used by the Razorpay
  // Standard Checkout hook (it closes from inside the verify handler's `finally`,
  // where a guarded close would be blocked by the not-yet-flushed launch state).
  const resetAndClose = () => {
    setUserSelectedMethod(restrictToMethod ?? 'card')
    setBankTransferOpen(false)
    setIsAddCardOpen(false)
    setUserSelectedAmount(null)
    setSelectedCardId(0)
    onClose()
  }

  // A RAZORPAY-client saved card must open Razorpay Standard Checkout regardless of
  // which gateway this modal itself belongs to. Detect their
  // presence to warm the SDK; handlePay routes to Standard Checkout for them.
  const hasRazorpayCard = activeCards.some(isRazorpaySavedCard)
  const {
    launchStandardCheckout: launchRazorpayStandardCheckout,
    isLaunching: isRazorpayLaunching,
    isCreatingOrder: isRazorpayCreatingOrder,
  } = useRazorpayStandardCheckout({
    onClose: resetAndClose,
    warm: isOpen && hasRazorpayCard,
    isTrial,
  })

  const isPending =
    creatingOrder ||
    cashfreePayPending ||
    isPreparingBankTransfer ||
    isLaunchingCheckout ||
    isRazorpayLaunching ||
    isRazorpayCreatingOrder

  const cardFeeLabel = getCardFeeLabel(rechargeDetails?.gateway_fee_rate)

  // One-time card recharge adds the gateway fee ON TOP of the chosen amount
  // (payment-service: totalCharge = amount + fee), so the customer is debited
  // more than they top up. Surface the breakup before they commit. Netbanking /
  // bank transfer are zero-fee, so this only renders for card.
  const gatewayFeeRate = rechargeDetails?.gateway_fee_rate
  const showCardFeeBreakup = method === 'card' && !!gatewayFeeRate && selectedAmount > 0
  const cardFeeAmount = (selectedAmount * (gatewayFeeRate ?? 0)) / 100
  const cardTotalCharge = selectedAmount + cardFeeAmount

  const handleClose = () => {
    // Block close while an order is being created or a hosted checkout (Cashfree
    // OR Razorpay) is launching — closing here doesn't cancel the inflight request
    // and the user can get yanked into the hosted page after a "Cancel".
    if (isPending) return
    resetAndClose()
  }

  const handlePay = async () => {
    if (isPending || !selectedAmount) return

    // Resolve the selected card before the initiation event: a RAZORPAY-client card
    // routes to Standard Checkout below, so the funnel has to be labelled with that
    // gateway instead of a flat 'cashfree'.
    const selectedCardObj = activeCards.find((c) => c.card_mapping_id === effectiveSelectedCardId)

    // Funnel start — fires for every method before any hosted-checkout redirect.
    // sendBeacon guarantees delivery across the _self redirect (netbanking /
    // legacy card), same as recharge-modal.tsx.
    track(
      AnalyticsEvents.RECHARGE_INITIATED,
      {
        amount: selectedAmount,
        currency,
        method,
        is_trial: isTrial,
        gateway: rechargeGatewayLabel(selectedCardObj, true),
      },
      { transport: 'sendBeacon' },
    )

    try {
      if (method === 'card' && isCashfreeProvider) {
        // S2S saved-card recharge → 3DS via payment_url. New cards go through
        // AddCardModal, not here.
        if (!effectiveSelectedCardId) return
        // A RAZORPAY-client saved card opens Razorpay Standard Checkout (hosted,
        // PCI-safe) instead of the Cashfree S2S charge — independent of the
        // this modal's own gateway. Cashfree/Stripe cards keep the path below. The
        // hook emits CREDITS_RECHARGED on success, standing in for the completion
        // event this return skips.
        if (isRazorpaySavedCard(selectedCardObj)) {
          await launchRazorpayStandardCheckout(selectedAmount)
          return
        }
        const result = await cashfreePay({
          account_id: accountId,
          amount: selectedAmount,
          product_type: 'plivo',
          card_mapping_id: effectiveSelectedCardId,
        })
        if (result.payment_url) {
          window.location.href = result.payment_url
          return
        }
        // No `payment_url` from the saved-card S2S path — the upstream
        // recoded the charge but didn't hand back a 3DS redirect. Surface
        // a generic error so the user retries instead of seeing the modal
        // close silently.
        throw new Error('saved-card-no-payment-url')
      } else if (method === 'card' || method === 'netbanking') {
        // Defense-in-depth — the CTA is already disabled below the floor.
        if (method === 'netbanking' && netbankingDisabledReason) return
        // Legacy hosted-checkout via /cashfree/order/create (card pre-rollout + netbanking).
        const order = await createOrder({
          account_id: accountId,
          amount: selectedAmount,
          product_type: 'plivo',
          payment_method_type: method,
          return_url: buildPaymentCallbackUrl(method),
          // Trial account: this order buys Professional. LOAD-BEARING for
          // netbanking, which settings/payment-modes opens DIRECTLY
          // (restrictToMethod="netbanking") without going through the provider
          // fork — and the Payment methods tab is visible on the free plan. So an
          // INR trial could pay by netbanking here and stay on free_trial forever;
          // a redirect rail has no other mechanism. See ../upgrade-plan.
          ...buildUpgradePlanPatch(isTrial),
        })
        if (!order.payment_session_id) {
          throw new Error('order-no-payment-session-id')
        }
        setIsLaunchingCheckout(true)
        if (isEmbeddedCardCheckoutEnabled && method === 'card') {
          // Card only: the PAN is entered inside Cashfree's in-page hosted modal
          // (PCI-safe). Netbanking carries no card data, so it keeps the legacy _self
          // redirect below. The modal resolves on close; we then confirm the outcome
          // server-side (no redirect, no /payment/callback round-trip).
          await launchCashfreeHostedCheckout(order.payment_session_id, true)
          const status = await verifyAndPollCashfreePayment(order.order_id)
          // The credit lands via Cashfree's async webhook, so poll until the
          // balance actually moves rather than invalidating once against a value
          // the backend hasn't updated yet. PENDING means it is explicitly still
          // confirming, so that case gets the longer window.
          void refreshBillingAfterPayment(queryClient, accountId, {
            ...(status === 'PENDING'
              ? { maxAttempts: BILLING_REFRESH_PROCESSING_MAX_POLL_ATTEMPTS }
              : {}),
          })
          if (isCashfreePaid(status)) {
            track(AnalyticsEvents.CREDITS_RECHARGED, {
              amount: selectedAmount,
              currency,
              is_trial: isTrial,
            })
            successToast({ title: 'Success', description: 'Payment successful' })
          } else if (status === 'PENDING') {
            // The in-page path never hits /payment/callback, so mirror its
            // resolution vocabulary here — else the funnel is blind to embedded
            // outcomes (resolutions with no matching initiations).
            track(AnalyticsEvents.PAYMENT_CALLBACK_RESOLVED, {
              status_bucket: 'processing',
              raw_status: status,
              method: 'card',
              source: 'cashfree_inpage',
            })
            infoToast({
              title: 'Payment processing',
              description: 'Your payment is being confirmed and will reflect shortly.',
            })
          } else if (!isCashfreeUserCancelled(status)) {
            track(AnalyticsEvents.PAYMENT_FAILED, {
              flow: 'recharge',
              method: 'card',
              amount: selectedAmount,
              currency,
              raw_status: status,
            })
            // Stay quiet on a user-initiated cancel; only surface genuine failures.
            errorToast({ title: 'Payment Failed', description: 'Please try again.' })
          } else {
            track(AnalyticsEvents.PAYMENT_CALLBACK_RESOLVED, {
              status_bucket: 'dropped',
              raw_status: status,
              method: 'card',
              source: 'cashfree_inpage',
            })
          }
          setIsLaunchingCheckout(false)
          setUserSelectedMethod(restrictToMethod ?? 'card')
          setUserSelectedAmount(null)
          setSelectedCardId(0)
          onClose()
          return
        }
        // Legacy: full-tab redirect to the hosted page; returns via /payment/callback.
        await launchCashfreeHostedCheckout(order.payment_session_id)
      } else if (method === 'bank_transfer') {
        // Defense-in-depth — the CTA is already disabled below the floor.
        if (bankTransferDisabledReason) return
        setIsPreparingBankTransfer(true)
        try {
          await ensureActiveVbank(selectedAmount)
          setBankTransferOpen(true)
        } finally {
          setIsPreparingBankTransfer(false)
        }
      }
    } catch (error) {
      // Don't surface backend-specific failure text — it leaks internals
      // ("Failed to create Cashfree customer" etc.). Keep the detail in the
      // logger for debugging and show the customer a generic retry message.
      logger.error('CashfreeRechargeModal: handlePay failed', { method, error })
      errorToast({
        title:
          method === 'bank_transfer' ? 'Could not load bank transfer details' : 'Payment Failed',
        description: 'We faced an error. Please try again in some time.',
      })
      setIsLaunchingCheckout(false)
      // Tracking last so a throw here can't skip the loading-state reset above.
      trackPaymentFailed('recharge', error, {
        method,
        amount: selectedAmount,
        currency,
        gateway: 'cashfree',
      })
    }
  }

  const ctaDisabledReason = resolveRechargeCtaBlocker({
    selectedAmount,
    method,
    netbankingDisabledReason,
    bankTransferDisabledReason,
    cardBlocker:
      method === 'card' && isCashfreeProvider && activeCards.length === 0
        ? 'Add a card to continue'
        : undefined,
  })
  const ctaDisabled = isPending || ctaDisabledReason !== undefined

  const ctaLabel = method === 'bank_transfer' ? 'View Details' : 'Pay'

  const dialogTitle = (() => {
    if (restrictToMethod === 'card') return 'Add Card'
    if (restrictToMethod === 'netbanking') return 'Pay via Netbanking'
    return 'Add Balance'
  })()

  return (
    <>
      {/* Suppress the recharge dialog while the Add Card modal is open so the two
          don't stack (same pattern as !bankTransferOpen). Radix doesn't fire
          onOpenChange for this controlled change, so handleClose isn't triggered. */}
      <Dialog
        open={isOpen && !bankTransferOpen && !isAddCardOpen}
        // Non-modal while Razorpay's hosted checkout is open so its <body> iframe
        // isn't trapped in Radix's inert region. handleClose no-ops mid-launch
        // (isPending includes isRazorpayLaunching), so the user-initiated close is
        // already blocked.
        modal={!isRazorpayLaunching}
        onOpenChange={(open) => !open && handleClose()}
      >
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            <RechargeAmountTile
              title="Select Amount"
              selectedAmount={selectedAmount}
              onSelect={setUserSelectedAmount}
              rechargeChoicesData={{
                choicesList: tileChoices || [],
                currency,
              }}
              disabled={isPending}
            />

            {showCardFeeBreakup && (
              <p
                className="text-s-400 text-muted-foreground"
                data-testid="cashfree-card-fee-breakup"
              >
                A {gatewayFeeRate}% payment gateway fee (
                {formatCurrency(cardFeeAmount, currency, false)}) applies. You&apos;ll be charged{' '}
                {formatCurrency(cardTotalCharge, currency, false)} —{' '}
                {formatCurrency(selectedAmount, currency, false)} credited to your balance.
              </p>
            )}

            {!restrictToMethod && (
              <div>
                <h4 className="mb-3 text-p-600">Select Payment Mode</h4>
                <div className="flex flex-col gap-2">
                  <MethodTile
                    method="card"
                    selected={method}
                    onSelect={setUserSelectedMethod}
                    icon={<CreditCard className="size-5" />}
                    title="Card"
                    subtitle="Pay using credit or debit card"
                    badge={cardFeeLabel}
                    disabled={isPending}
                  />
                  <MethodTile
                    method="netbanking"
                    selected={method}
                    onSelect={setUserSelectedMethod}
                    icon={<Landmark className="size-5" />}
                    title="Netbanking"
                    subtitle={netbankingDisabledReason ?? "Pay using your bank's net banking"}
                    badge={ZERO_FEE_LABEL}
                    disabled={isPending || netbankingDisabledReason !== undefined}
                  />
                  {isEnterprisePlan && isCashFreeBankTransferEnabled && (
                    <MethodTile
                      method="bank_transfer"
                      selected={method}
                      onSelect={setUserSelectedMethod}
                      icon={<Banknote className="size-5" />}
                      title="Bank Transfer"
                      subtitle={bankTransferDisabledReason ?? 'Transfer via IMPS, NEFT, or RTGS'}
                      badge={ZERO_FEE_LABEL}
                      disabled={isPending || bankTransferDisabledReason !== undefined}
                    />
                  )}
                </div>
              </div>
            )}

            {method === 'card' && isCashfreeProvider && (
              <CardSelector
                title="Cards"
                activeCards={activeCards}
                selectedCard={effectiveSelectedCardId}
                onCardChange={setSelectedCardId}
                onAddCard={() => setIsAddCardOpen(true)}
                isPending={isPending}
                showAddCardButton={activeCards.length > 0}
              />
            )}
          </div>

          <DialogFooter className="flex justify-between sm:justify-between">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isPending}
              data-testid="cashfree-recharge-cancel-btn"
            >
              Cancel
            </Button>
            <ButtonWithLoader
              type="button"
              disabled={ctaDisabled}
              disabledReason={ctaDisabledReason}
              loading={isPending}
              onClick={handlePay}
              data-testid="cashfree-recharge-pay-btn"
            >
              {ctaLabel}
            </ButtonWithLoader>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <CashfreeBankTransferDrawer open={bankTransferOpen} onClose={handleClose} />
      {/* First-time users (no cards + no billing address) get the address step. */}
      {isCashfreeProvider &&
        (isFirstCardAdd ? (
          <AddFirstCard isOpen={isAddCardOpen} onClose={() => setIsAddCardOpen(false)} />
        ) : (
          <AddCardModal isOpen={isAddCardOpen} onClose={() => setIsAddCardOpen(false)} />
        ))}
    </>
  )
}
